// Le tirage des équipes (D-175 lot 1, D-176 ; maquettes t1 à t3).
//  * t1 : la fiche « Pour les tirages » sur la carte d'une joueuse (staff de l'équipe et responsable technique) : le niveau
//    (Découverte, À l'aise, Confirmé ; 1 à 3 étoiles), un ou deux postes (à partir des U13), fille ou garçon dans un
//    groupe mixte ; « Staff seulement · effacé en fin de saison ».
//  * t2 : le tirage (les groupes d'un exercice de « Composer la séance », Le Parcours, Le Sablier) : qui joue, combien,
//    les critères, le résultat dans les chasubles avec ce que seul le coach voit (postes, étoiles, écart), « Relancer »,
//    « Verrouiller », « Montrer le tirage sur la TV ».
//  * t3 : le tirage sur la TV : le plein écran de l'appareil du coach (à recopier sur la TV), sombre comme les jeux, les
//    cartes qui se retournent une à une dans leur chasuble avec le son des jeux ; des prénoms (ou les numéros), jamais
//    une étoile, un niveau ni un poste.
// L'algorithme : src/domain/tirage.ts ; la base : src/lib/tirage-api.ts.
import './tirage.css';
import {
  DEFAULT_CRITERIA, LEVELS, POSTES, TEAMS_MAX, TEAMS_MIN, ageLabel, draw, drawSummary, levelLabel, partitionKey, posteLabel, postesProposed,
  readCriteria, seasonEndYear, setLink, sexProposed, sizesFor, teamsFor, togglePoste, youngestYear,
  type DrawCriteria, type DrawPlayer, type DrawProfile, type DrawResult, type Level, type Poste,
} from '../domain/tirage';
import { BIB_HEX, BIB_INK, type Bib } from '../domain/groups';
import { agree, playerNoun, type TeamGender } from '../domain/labels';
import { drawPool, saveDrawProfile } from '../lib/tirage-api';
import { $, $$, html, mount, raw, toast, type Html } from './dom';
import { clubLogo, esc, jersey } from './game-art';
import { engage } from './drill-engine';
import { setGameSound, sndFanfare, sndFlip } from './game-sounds';

const ICON = {
  shuffle: '<svg class="tr-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h3.5c4 0 5.5 10 9.5 10H21M3 17h3.5c1.6 0 2.7-1.6 3.6-3.6M13.9 9.6C14.8 8 15.6 7 17 7h4M18 4l3 3-3 3M18 14l3 3-3 3"/></svg>',
  tv: '<svg class="tr-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="12" rx="2"/><path d="M8 21h8M12 17v4"/></svg>',
  lock: '<svg class="tr-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>',
  check: '<svg class="tr-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>',
  warn: '<svg class="tr-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4 2.8 20h18.4z"/><path d="M12 10v4.5M12 17.5v.5"/></svg>',
};

/** 1 à 3 étoiles (les éteintes en gris). */
export const stars = (n: number, max = 3): Html => html`<span class="tr-stars" role="img" aria-label="${n} étoile${n > 1 ? 's' : ''}">${Array.from({ length: max }, (_, i) => html`<span class="ti ti-star ${i < n ? '' : 'off'}" aria-hidden="true"></span>`)}</span>`;

/** Le nom d'un groupe d'après sa chasuble, accordé à l'équipe : « Rouges », « Bleues ». */
export function bibName(bib: Bib, g: TeamGender): string {
  const F: Record<Bib, string> = { red: 'Rouges', blue: 'Bleues', yellow: 'Jaunes', green: 'Vertes', orange: 'Orange', purple: 'Violettes' };
  const M: Record<Bib, string> = { red: 'Rouges', blue: 'Bleus', yellow: 'Jaunes', green: 'Verts', orange: 'Orange', purple: 'Violets' };
  return (g === 'F' ? F : M)[bib];
}

// =================================================================================================================
// t1 · La fiche « Pour les tirages », sur la carte d'une joueuse
// =================================================================================================================
export interface FicheTeam { id: string; gender: TeamGender; category: string | null }

export function ficheHtml(team: FicheTeam, first: string, p: DrawProfile): Html {
  const g = team.gender;
  return html`<section class="tr-tir" data-guide="tr-fiche" aria-label="Pour les tirages">
    <h4>${raw(ICON.shuffle)} Pour les tirages <span class="tr-lockchip">${raw(ICON.lock)}Staff seulement</span></h4>
    <div><p class="tr-lab">Niveau</p>
      <div class="tr-lv" role="group" aria-label="Niveau" data-guide="tr-fiche-lv">${LEVELS.map(l => html`<button type="button" data-lv="${l}" aria-pressed="${String(p.level === l)}">${stars(l)}<b>${levelLabel(l, g)}</b></button>`)}</div></div>
    ${postesProposed(team.category) ? html`<div><p class="tr-lab">Postes · un ou deux</p>
      <div class="tr-postes" role="group" aria-label="Postes">${POSTES.map(k => html`<button type="button" class="chip-toggle" data-poste="${k}" aria-pressed="${String(p.positions.includes(k))}"><i>${k}</i>${posteLabel(k, g)}</button>`)}</div></div>` : ''}
    ${sexProposed(g) ? html`<div><p class="tr-lab">Dans le groupe mixte</p>
      <div class="seg tr-sex" role="group" aria-label="Fille ou garçon"><button type="button" data-sex="F" aria-pressed="${String(p.sex === 'F')}">Fille</button><button type="button" data-sex="M" aria-pressed="${String(p.sex === 'M')}">Garçon</button></div></div>` : ''}
    <p class="tr-keep" data-guide="tr-fiche-keep">${raw(ICON.lock)}<span><b>Visible du staff seulement · effacé en fin de saison.</b> Ni ${first}, ni ses parents, ni les autres équipes ne le voient ; jamais sur la TV ni sur la page publique. Une seule valeur, sans historique : la nouvelle remplace l’ancienne.</span></p>
    <button type="button" class="btn btn-primary" data-fiche-save>Enregistrer</button>
  </section>
  <p class="hint tr-fiche-hint">Les postes se notent à partir des U13 (jusqu’en U11, on ne les propose pas). Dans un groupe mixte, la fiche demande aussi fille ou garçon. Pas envie de noter ? Tu peux donner les niveaux au moment du tirage, oubliés ensuite.</p>`;
}

/** Les gestes de la fiche : toucher un niveau (le retoucher l'enlève), un poste (deux au plus), fille ou garçon ; « Enregistrer ». */
export function wireFiche(sheet: HTMLElement, team: FicheTeam, personId: string, initial: DrawProfile): void {
  const box = $(sheet, '.tr-tir');
  if (!box) return;
  let p: DrawProfile = { ...initial, positions: [...initial.positions] };
  const paint = () => {
    for (const b of $$<HTMLButtonElement>(box, '[data-lv]')) b.setAttribute('aria-pressed', String(p.level === Number(b.dataset.lv)));
    for (const b of $$<HTMLButtonElement>(box, '[data-poste]')) b.setAttribute('aria-pressed', String(p.positions.includes(b.dataset.poste as Poste)));
    for (const b of $$<HTMLButtonElement>(box, '[data-sex]')) b.setAttribute('aria-pressed', String(p.sex === b.dataset.sex));
  };
  for (const b of $$<HTMLButtonElement>(box, '[data-lv]')) b.addEventListener('click', () => { const l = Number(b.dataset.lv) as Level; p = { ...p, level: p.level === l ? null : l }; paint(); });
  for (const b of $$<HTMLButtonElement>(box, '[data-poste]')) b.addEventListener('click', () => { p = { ...p, positions: togglePoste(p.positions, b.dataset.poste as Poste) }; paint(); });
  for (const b of $$<HTMLButtonElement>(box, '[data-sex]')) b.addEventListener('click', () => { const s = b.dataset.sex as 'F' | 'M'; p = { ...p, sex: p.sex === s ? null : s }; paint(); });
  $<HTMLButtonElement>(box, '[data-fiche-save]')!.addEventListener('click', async e => {
    const b = e.currentTarget as HTMLButtonElement;
    b.disabled = true;
    try { await saveDrawProfile(team.id, personId, p); toast('Enregistré pour les tirages'); }
    catch { toast('Enregistrement impossible.', 'ko'); }
    finally { b.disabled = false; }
  });
}

// =================================================================================================================
// t2 · Le tirage
// =================================================================================================================
export interface TirageWho { id: string; name: string; jersey: number | null }
export interface TirageOpts {
  team: { id: string; name: string; gender: TeamGender; category: string | null };
  /** Tout le monde qui peut jouer (dans l'ordre d'affichage). */
  people: TirageWho[];
  /** Cochées au départ (les présentes) ; les autres sont barrées, un toucher les ajoute. */
  present: string[];
  teams: number;
  /** Les critères dans un repli fermé (Le Parcours, Le Sablier). */
  folded?: boolean;
  lockLabel?: string;
  lockHint?: string;
  tv: { title: string; sub: string };
  /** « Verrouiller » : le tirage et le nom de chaque chasuble. */
  onLock: (r: DrawResult) => void;
}

interface Saved { criteria: DrawCriteria; mode: 'count' | 'per'; per: number }
const KEY = (team: string) => `arceau.tirage.${team}`;
function loadSaved(team: string): Saved | null {
  try {
    const v = JSON.parse(localStorage.getItem(KEY(team)) ?? 'null') as Partial<Saved> | null;
    return v ? { criteria: readCriteria(v.criteria), mode: v.mode === 'per' ? 'per' : 'count', per: Math.max(1, Math.min(12, Number(v.per) || 4)) } : null;
  } catch { return null; }
}
function keepSaved(team: string, s: Saved): void { try { localStorage.setItem(KEY(team), JSON.stringify(s)); } catch { /* réglage de confort */ } }

export function mountTirage(host: HTMLElement, o: TirageOpts): void {
  const g = o.team.gender;
  const noun = (n: number) => playerNoun(g, n);
  const nameOf = new Map(o.people.map(p => [p.id, p.name]));
  const saved = loadSaved(o.team.id);
  let criteria: DrawCriteria = saved?.criteria ?? { ...DEFAULT_CRITERIA };
  let mode: 'count' | 'per' = saved?.mode ?? 'count';
  let per = saved?.per ?? 4;
  let count = Math.max(TEAMS_MIN, Math.min(TEAMS_MAX, o.teams || 2));
  const here = new Set(o.present.filter(id => nameOf.has(id)));
  const temp: Record<string, Level> = {};
  let seed = (Date.now() ^ Math.floor(Math.random() * 1e9)) >>> 0;
  let avoid: string[][] | null = null;
  let result: DrawResult | null = null;
  let info = new Map<string, Omit<DrawPlayer, 'id'>>();
  let loaded = false;
  let unrOpen = false;
  let linking: string[] | null = null;
  let numbers = false;
  const keep = () => keepSaved(o.team.id, { criteria, mode, per });

  const players = (): DrawPlayer[] => o.people.filter(p => here.has(p.id)).map(p => ({ id: p.id, level: null, postes: [], sex: null, birthYear: null, ...info.get(p.id) }));
  const k = () => teamsFor(here.size, mode, mode === 'per' ? per : count);
  const endYear = seasonEndYear(new Date());
  const young = () => youngestYear(players());
  // « U12 » dans une catégorie de jeunes ; chez les seniors, « une plus jeune ».
  const youngTxt = () => { const y = young(); return y === null || !/^U\d+$/.test(o.team.category ?? '') ? null : ageLabel(y, endYear); };
  const run = () => {
    const ps = players();
    result = ps.length ? draw({ players: ps, teams: k(), criteria: { ...criteria, postes: postesProposed(o.team.category) ? criteria.postes : 'ignore', mixite: sexProposed(g) ? criteria.mixite : 'ignore' }, temp, seed, avoid }) : null;
  };

  const seg = (c: keyof DrawCriteria, opts: [string, string][], wrap = false) => html`<div class="seg ${wrap ? 'tr-wrap' : ''}" role="group">${opts.map(([v, l]) =>
    html`<button type="button" data-c="${c}" data-v="${v}" aria-pressed="${String(criteria[c] === v)}">${l}</button>`)}</div>`;
  const linkChip = (p: [string, string], kind: 'apart' | 'together') => html`<button type="button" class="tr-link ${kind}" data-unlink="${p[0]}|${p[1]}" aria-label="Retirer le lien ${nameOf.get(p[0]) ?? ''} ${kind === 'apart' ? agree(g, 'séparé de', 'séparée de') : 'avec'} ${nameOf.get(p[1]) ?? ''}">${nameOf.get(p[0]) ?? '?'} ${kind === 'apart' ? '≠' : '+'} ${nameOf.get(p[1]) ?? '?'}</button>`;
  const critBox = () => html`<div class="tr-crit">
      <div><b>Niveau</b>${seg('level', [['balance', 'Équilibrer'], ['ignore', 'Ignorer']])}</div>
      <div><b>Postes</b>${postesProposed(o.team.category) ? seg('postes', [['spread', 'Ventiler'], ['ignore', 'Ignorer']]) : html`<span class="dis">Ignorés jusqu’en U11</span>`}</div>
      <div><b>Catégorie</b>${seg('category', [['mix', 'Mélanger'], ['age', 'Par âge'], ['young', `${agree(g, 'Un', 'Une')} ${youngTxt() ?? 'plus jeune'} par équipe`]], true)}</div>
      <div><b>Mixité</b>${sexProposed(g) ? seg('mixite', [['balance', 'Équilibrer'], ['ignore', 'Ignorer']]) : html`<span class="dis">Proposée dans un groupe mixte</span>`}</div>
      <div><b>Liens</b><div class="lnk">${criteria.apart.filter(p => nameOf.has(p[0]) && nameOf.has(p[1])).map(p => linkChip(p, 'apart'))}${criteria.together.filter(p => nameOf.has(p[0]) && nameOf.has(p[1])).map(p => linkChip(p, 'together'))}
        <button type="button" class="tr-addlink" data-link>+ Ensemble ou séparer</button></div></div>
    </div>
    ${linking ? html`<div class="tr-linker"><p class="hint">Touche deux prénoms${linking.length ? ` · ${linking.map(id => nameOf.get(id)).join(' et ')}` : ''}.</p>
      <div class="tr-who">${o.people.filter(p => here.has(p.id)).map(p => html`<button type="button" data-lk="${p.id}" aria-pressed="${String(linking!.includes(p.id))}">${p.name}</button>`)}</div>
      <div class="btn-row"><button type="button" class="btn btn-ghost btn-sm" data-lkset="together" ${linking.length === 2 ? '' : 'disabled'}>Ensemble</button>
        <button type="button" class="btn btn-ghost btn-sm" data-lkset="apart" ${linking.length === 2 ? '' : 'disabled'}>Séparer</button>
        <button type="button" class="btn btn-link btn-sm" data-lkx>Annuler</button></div></div>` : ''}`;
  const critLine = () => [criteria.level === 'balance' ? 'niveau équilibré' : 'niveau ignoré',
    postesProposed(o.team.category) ? (criteria.postes === 'spread' ? 'postes ventilés' : 'postes ignorés') : null,
    criteria.category === 'mix' ? 'âges mélangés' : criteria.category === 'age' ? 'par âge' : `${agree(g, 'un', 'une')} ${youngTxt() ?? 'plus jeune'} par équipe`,
    criteria.apart.length + criteria.together.length ? `${criteria.apart.length + criteria.together.length} lien${criteria.apart.length + criteria.together.length > 1 ? 's' : ''}` : null].filter(Boolean).join(' · ');

  const chip = (id: string) => {
    const x = info.get(id);
    const lv = temp[id] ?? x?.level ?? null;
    return html`<span class="tr-pl ${lv === null ? 'nolvl' : ''}"><span>${nameOf.get(id) ?? '—'}</span><small>${x?.postes.length ? html`<i>${x.postes.join('')}</i>` : ''}${stars(lv ?? 2)}${lv === null ? html`<em>sans niveau</em>` : ''}</small></span>`;
  };
  const resultBox = (): Html => {
    if (!loaded) return html`<p class="muted small">Le tirage se prépare…</p>`;
    if (!result || here.size < 2) return html`<p class="muted small">Coche au moins deux ${noun(2)}.</p>`;
    const r = result;
    const sum = drawSummary(r, criteria, g, id => nameOf.get(id) ?? '?', youngTxt());
    const sizes = sizesFor(here.size, r.teams.length);
    const unr = r.unrated;
    return html`
      <div class="tr-bal ${sum.ok ? '' : 'warn'}">${raw(sum.ok ? ICON.check : ICON.warn)}<span><b>${sum.head}</b> ${sum.text}${Math.max(...sizes) !== Math.min(...sizes) && criteria.level === 'balance' ? ' Écart compté à effectif égal.' : ''}${r.same ? ' Pas d’autre tirage aussi juste.' : ''}</span></div>
      ${unr.length || Object.keys(temp).length ? html`<div class="tr-unr">
        ${unr.length ? html`<span class="chip chip-warn">${unr.length} sans niveau</span><span class="muted small">${agree(g, 'compté', 'comptée', unr.length)} « À l’aise »</span>` : html`<span class="muted small">Niveaux donnés pour ce tirage seulement.</span>`}
        <button type="button" class="btn btn-link btn-sm" data-unr aria-expanded="${String(unrOpen)}">${unrOpen ? 'Replier' : 'Leur donner un niveau'}</button></div>
        ${unrOpen ? html`<div class="tr-temp">${o.people.filter(p => here.has(p.id) && (info.get(p.id)?.level ?? null) === null).map(p => html`<div><span>${p.name}</span>
          <span class="tr-tempbtns" role="group" aria-label="Niveau de ${p.name} pour ce tirage">${LEVELS.map(l => html`<button type="button" data-temp="${p.id}" data-l="${l}" aria-pressed="${String(temp[p.id] === l)}" aria-label="${levelLabel(l, g)}">${stars(l)}</button>`)}</span></div>`)}
          <p class="hint">Pour ce tirage seulement : oublié ensuite. Pour le garder, la fiche « Pour les tirages » sur la carte de chacune, dans Équipe.</p></div>` : ''}` : ''}
      <div class="tr-res gx-groups">${r.teams.map(t => html`<section class="gx-g" style="--bib:${BIB_HEX[t.bib]}">
        <header><span class="gx-bib" style="background:${BIB_HEX[t.bib]};color:${BIB_INK[t.bib]}" aria-hidden="true"></span><strong>${bibName(t.bib, g)}</strong>
          <span class="tot" role="img" aria-label="${t.stars} étoiles">${t.stars} <span class="ti ti-star" aria-hidden="true"></span></span><span class="gx-n">${t.players.length}</span></header>
        <div class="gx-chips">${t.players.map(chip)}</div></section>`)}</div>
      <div class="tr-drawbtns" data-guide="tr-lock"><button type="button" class="btn btn-ghost" data-relance>${raw(ICON.shuffle)} Relancer</button>
        <button type="button" class="btn btn-primary" data-lock>${raw(ICON.lock)} ${o.lockLabel ?? 'Verrouiller'}</button></div>
      <div class="tr-tvrow"><button type="button" class="btn btn-ghost btn-sm tr-tvbtn" data-tv>${raw(ICON.tv)} Montrer le tirage sur la TV</button>
        <label class="tr-numonly"><input type="checkbox" data-num ${numbers ? 'checked' : ''}> Numéros seulement</label></div>
      <p class="hint">${o.lockHint ?? `Verrouillé, le tirage devient les groupes de l’exercice, avec leurs chasubles ; ${agree(g, 'un joueur', 'une joueuse')} se glisse encore d’un groupe à l’autre à la main. Relancer donne un autre tirage aussi juste.`}</p>`;
  };

  const paint = () => {
    const sizes = here.size ? sizesFor(here.size, k()) : [0];
    const sz = Math.min(...sizes) === Math.max(...sizes) ? `${sizes[0]}` : `${Math.min(...sizes)} ou ${Math.max(...sizes)}`;
    const absent = o.people.length - here.size;
    mount(host, html`<div class="tr-panel">
      <section class="tr-sec" data-guide="tr-who"><h5>Qui joue <small>${here.size} ${agree(g, 'présent', 'présente', here.size)} sur ${o.people.length}</small></h5>
        <div class="tr-who">${o.people.map(p => html`<button type="button" data-who="${p.id}" aria-pressed="${String(here.has(p.id))}" ${here.has(p.id) ? '' : html`class="off"`}>${p.name}</button>`)}</div>
        ${absent ? html`<p class="hint">${agree(g, 'Barré', 'Barrée')} : pas ${agree(g, 'tiré', 'tirée')}. Un toucher l’ajoute ou ${agree(g, 'le', 'la')} retire.</p>` : ''}</section>
      <section class="tr-sec"><h5>Combien</h5>
        <div class="tr-count"><span class="tr-step"><button type="button" data-cnt="-1" aria-label="${mode === 'per' ? 'Une de moins par équipe' : 'Une équipe de moins'}">−</button><b>${mode === 'per' ? per : k()}</b><button type="button" data-cnt="1" aria-label="${mode === 'per' ? 'Une de plus par équipe' : 'Une équipe de plus'}">+</button></span>
          <span>${mode === 'per' ? 'par équipe' : 'équipes'}</span>
          <button type="button" class="tr-alt" data-mode>${mode === 'per' ? `soit ${k()} équipes · ou « ${k()} équipes »` : `de ${sz} ${noun(Math.max(...sizes))} · ou « ${Math.max(1, Math.min(...sizes))} par équipe »`}</button></div></section>
      ${o.folded ? html`<details class="tr-critfold" data-guide="tr-crit"><summary><b>Critères</b><small>${critLine()}</small></summary>${critBox()}</details>`
        : html`<section class="tr-sec" data-guide="tr-crit"><h5>Critères <small>le reste est replié</small></h5>${critBox()}</section>`}
      <section class="tr-sec tr-result" data-guide="tr-result"><h5>Le tirage <small>les étoiles : sur ton téléphone seulement</small></h5>${resultBox()}</section>
    </div>`);
    wire();
  };
  const redo = (fresh = false) => { if (fresh) { avoid = result ? result.teams.map(t => t.players) : null; seed = (seed * 1103515245 + 12345) >>> 0; } run(); paint(); };

  const wire = () => {
    const folded = $<HTMLDetailsElement>(host, '.tr-critfold');
    for (const b of $$<HTMLButtonElement>(host, '[data-who]')) b.addEventListener('click', () => {
      const id = b.dataset.who!;
      if (here.has(id)) here.delete(id); else here.add(id);
      avoid = null; redo();
    });
    for (const b of $$<HTMLButtonElement>(host, '[data-cnt]')) b.addEventListener('click', () => {
      const d = Number(b.dataset.cnt);
      if (mode === 'per') per = Math.max(1, Math.min(12, per + d)); else count = Math.max(TEAMS_MIN, Math.min(TEAMS_MAX, k() + d));
      keep(); avoid = null; redo();
    });
    $(host, '[data-mode]')?.addEventListener('click', () => {
      const sizes = sizesFor(here.size, k());
      if (mode === 'count') { per = Math.max(1, Math.min(...sizes)); mode = 'per'; } else { count = k(); mode = 'count'; }
      keep(); redo();
    });
    for (const b of $$<HTMLButtonElement>(host, '[data-c]')) b.addEventListener('click', () => {
      criteria = { ...criteria, [b.dataset.c!]: b.dataset.v } as DrawCriteria;
      keep(); avoid = null; redo();
      if (folded) $<HTMLDetailsElement>(host, '.tr-critfold')!.open = true;
    });
    for (const b of $$<HTMLButtonElement>(host, '[data-unlink]')) b.addEventListener('click', () => {
      const [a, c] = b.dataset.unlink!.split('|') as [string, string];
      criteria = setLink(criteria, a, c, null); keep(); redo();
      if (folded) $<HTMLDetailsElement>(host, '.tr-critfold')!.open = true;
    });
    $(host, '[data-link]')?.addEventListener('click', () => { linking = []; paint(); if (folded) $<HTMLDetailsElement>(host, '.tr-critfold')!.open = true; });
    for (const b of $$<HTMLButtonElement>(host, '[data-lk]')) b.addEventListener('click', () => {
      const id = b.dataset.lk!;
      linking = linking!.includes(id) ? linking!.filter(x => x !== id) : [...linking!, id].slice(-2);
      paint(); if (folded) $<HTMLDetailsElement>(host, '.tr-critfold')!.open = true;
    });
    for (const b of $$<HTMLButtonElement>(host, '[data-lkset]')) b.addEventListener('click', () => {
      if (linking?.length !== 2) return;
      criteria = setLink(criteria, linking[0]!, linking[1]!, b.dataset.lkset as 'apart' | 'together');
      linking = null; keep(); avoid = null; redo();
      if (folded) $<HTMLDetailsElement>(host, '.tr-critfold')!.open = true;
    });
    $(host, '[data-lkx]')?.addEventListener('click', () => { linking = null; paint(); if (folded) $<HTMLDetailsElement>(host, '.tr-critfold')!.open = true; });
    $(host, '[data-unr]')?.addEventListener('click', () => { unrOpen = !unrOpen; paint(); });
    for (const b of $$<HTMLButtonElement>(host, '[data-temp]')) b.addEventListener('click', () => {
      const id = b.dataset.temp!, l = Number(b.dataset.l) as Level;
      if (temp[id] === l) delete temp[id]; else temp[id] = l;
      avoid = null; redo();
    });
    $(host, '[data-relance]')?.addEventListener('click', () => redo(true));
    $(host, '[data-lock]')?.addEventListener('click', () => { if (result) o.onLock(result); });
    $<HTMLInputElement>(host, '[data-num]')?.addEventListener('change', e => { numbers = (e.target as HTMLInputElement).checked; });
    $(host, '[data-tv]')?.addEventListener('click', () => {
      if (!result) return;
      const num = new Map(o.people.map(p => [p.id, p.jersey]));
      showTirageTv({ title: o.tv.title, sub: o.tv.sub, numbers, teams: result.teams.map(t => ({ bib: t.bib, name: bibName(t.bib, g), players: t.players.map(id => ({ name: nameOf.get(id) ?? '—', num: num.get(id) ?? null })) })) });
    });
  };

  paint();
  void drawPool(o.team.id, o.people.map(p => p.id)).then(m => { info = m; loaded = true; run(); paint(); }).catch(() => { loaded = true; run(); paint(); });
}

/** Deux répartitions identiques (le tirage d'avant, sans l'ordre) ? */
export const sameDraw = (a: string[][], b: string[][]) => partitionKey(a) === partitionKey(b);

// =================================================================================================================
// t3 · Le tirage sur la TV
// =================================================================================================================
export interface TvTeam { bib: Bib; name: string; players: { name: string; num: number | null }[] }

export function showTirageTv(o: { title: string; sub: string; teams: TvTeam[]; numbers: boolean }): void {
  setGameSound(true);
  const reduce = (() => { try { return matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; } })();
  const order: [number, number][] = [];
  const most = Math.max(0, ...o.teams.map(t => t.players.length));
  for (let i = 0; i < most; i++) o.teams.forEach((t, ti) => { if (t.players[i]) order.push([ti, i]); });
  const total = order.length;
  const logo = clubLogo();
  const lg = logo ? `<img src="${esc(logo)}" alt="">` : '';
  const label = (p: TvTeam['players'][number]) => (o.numbers ? (p.num === null ? '—' : String(p.num)) : p.name);

  const host = document.createElement('div');
  host.className = 'tr-tv';
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-label', 'Tirage des équipes');
  host.style.setProperty('--n', String(o.teams.length));
  host.innerHTML = `
    <header class="tr-tvtop"><div class="l"><span class="lg">${lg}</span><span><b>Tirage des équipes</b><small>${esc(o.sub)}</small></span></div>
      <div class="r"><span class="tr-livechip"><i></i><span data-state>Tirage en cours</span></span><button type="button" class="tr-tvx" aria-label="Fermer le tirage">×</button></div></header>
    <div class="tr-draw">${o.teams.map((t, ti) => `<section class="tr-dcol" style="--c:${BIB_HEX[t.bib]};--ci:${BIB_INK[t.bib]}"><div class="h">${jersey({ color: BIB_HEX[t.bib], ink: BIB_INK[t.bib], letter: '' }, 40)}<b>${esc(t.name)}</b><small data-cnt="${ti}">0 / ${t.players.length}</small></div>
      <div class="tr-slots" style="--rows:${Math.ceil(t.players.length / 2)};--pcols:${Math.max(1, Math.min(3, t.players.length))}">${t.players.map((_, i) => `<div class="tr-pc empty" data-slot="${ti}-${i}"></div>`).join('')}</div></section>`).join('')}</div>
    <footer class="tr-deck"><div class="tr-stack" aria-hidden="true">${[0, 1, 2].map(i => `<span style="left:${i * 18}px;top:${i * 3}px;transform:rotate(${(i - 1) * 4}deg)"><span class="lg">${lg}</span></span>`).join('')}</div>
      <div class="txt"><b data-msg>Les cartes se retournent une à une</b><small>${o.numbers ? 'Chacune dans sa chasuble, avec le son des jeux. Les numéros seulement.' : 'Chacune dans sa chasuble, avec le son des jeux. Aucun niveau, aucune étoile à l’écran.'}</small></div>
      <div class="cnt"><b data-done>0 / ${total}</b>retournées</div></footer>`;
  document.body.append(host);
  const release = engage(host, 'Tirage des équipes');

  const fit = () => {
    const portrait = innerHeight > innerWidth;
    host.classList.toggle('portrait', portrait);
    const k = Math.max(1, o.teams.length);
    const longest = Math.max(4, ...o.teams.flatMap(t => t.players.map(p => label(p).length)));
    const colW = portrait ? (innerWidth - 52) / Math.max(1, Math.min(3, most)) : ((innerWidth - 48 - (k - 1) * 18) / k - 28) / 2;
    host.style.setProperty('--fs', `${Math.max(14, Math.min(46, Math.floor(colW / (longest * 0.52 + 0.6))))}px`);
  };
  fit();
  addEventListener('resize', fit);

  let n = 0, timer = 0, closed = false;
  const stack = host.querySelector<HTMLElement>('.tr-stack')!;
  const flip = () => {
    if (closed) return;
    if (n >= total) {
      host.querySelector('[data-state]')!.textContent = 'Tirage terminé';
      host.querySelector('.tr-livechip')!.classList.add('done');
      host.querySelector('[data-msg]')!.textContent = 'Les équipes sont faites !';
      stack.hidden = true;
      sndFanfare();
      return;
    }
    const [ti, i] = order[n]!;
    const t = o.teams[ti]!;
    host.querySelector('.tr-pc.new')?.classList.remove('new');
    const slot = host.querySelector<HTMLElement>(`[data-slot="${ti}-${i}"]`)!;
    slot.className = 'tr-pc back';
    slot.innerHTML = `<span class="lg">${lg}</span>`;
    sndFlip();
    const show = () => {
      if (closed) return;
      slot.className = `tr-pc new ${reduce ? '' : 'show'}`;
      slot.style.setProperty('--c', BIB_HEX[t.bib]); slot.style.setProperty('--ci', BIB_INK[t.bib]);
      slot.innerHTML = `${jersey({ color: BIB_HEX[t.bib], ink: BIB_INK[t.bib], letter: '' }, 56)}<b>${esc(label(t.players[i]!))}</b>`;
      n++;
      host.querySelector('[data-done]')!.textContent = `${n} / ${total}`;
      host.querySelector(`[data-cnt="${ti}"]`)!.textContent = `${Math.min(t.players.length, i + 1)} / ${t.players.length}`;
      const left = total - n;
      stack.querySelectorAll('span[style]').forEach((s, j) => { (s as HTMLElement).style.visibility = j < Math.min(3, left) ? '' : 'hidden'; });
      timer = window.setTimeout(flip, reduce ? 700 : 1100);
    };
    if (reduce) show(); else { slot.classList.add('flip'); timer = window.setTimeout(show, 420); }
  };
  timer = window.setTimeout(flip, 900);

  const close = () => {
    if (closed) return;
    closed = true;
    clearTimeout(timer);
    removeEventListener('resize', fit);
    release();
    host.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
  document.addEventListener('keydown', onKey);
  host.querySelector('.tr-tvx')!.addEventListener('click', close);
}

/** Les groupes déjà faits, montrés sur la TV de la même façon (« À la main »). */
export const tvFromGroups = (groups: { name: string; bib: Bib | null; players: string[] }[], nameOf: (id: string) => string, numOf: (id: string) => number | null): TvTeam[] =>
  groups.filter(gr => gr.bib && gr.players.length).map(gr => ({ bib: gr.bib!, name: gr.name, players: gr.players.map(id => ({ name: nameOf(id), num: numOf(id) })) }));
